import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    DndContext, DragOverlay, KeyboardSensor, PointerSensor,
    useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { FolderOpen } from 'lucide-react';
import { Surface } from '../layout/Surface';
import { cn } from '../../lib/utils';
import { createContainerCollisionDetection } from '../../lib/dndCollision';
import { getFileIcon, UNCATEGORIZED } from './fileMeta';
import FileRow, { DragHandle } from './FileRow';

/**
 * Artifacts grouped into category columns.
 *
 * Columns hold a floor width and the board scrolls sideways past it, rather than
 * dividing the surface into equal shares: a project can define any number of
 * custom categories, and eight equal shares of a laptop screen is eight columns
 * too narrow to read a filename in. Under the floor they stretch to fill, so the
 * common three-category case still looks like the Tasks kanban.
 *
 * Dragging a file between columns is the only place in the product where a
 * category is directly manipulable rather than picked from a Select.
 */
export default function FileShelf({
    files, categories, activeCategories, rowProps, openFileId, isChecked, readOnly, onSetCategory,
}) {
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor)
    );

    const { columns, byCategory } = useMemo(() => {
        // Filtering by category already empties the columns you didn't pick —
        // leaving them on screen just squeezes the ones you did.
        const cols = activeCategories.length
            ? categories.filter((c) => activeCategories.includes(c.id))
            : [...categories];

        // Orphan detection runs against every known category, not the visible
        // ones, so narrowing the board doesn't relabel a Docs file as
        // uncategorized.
        const known = new Set(categories.map((c) => c.id));
        const groups = new Map(cols.map((c) => [c.id, []]));
        const orphans = [];

        for (const f of files) {
            if (groups.has(f.category)) groups.get(f.category).push(f);
            else if (!known.has(f.category)) orphans.push(f);
        }

        // Chat writes `category: 'Code'`, which no category list defines. Those
        // files are invisible in the FileBrowser accordion for exactly this
        // reason — here they get a trailing column instead of silently
        // disappearing. It only exists when something is actually in it.
        if (orphans.length) {
            cols.push({ id: UNCATEGORIZED, label: 'Uncategorized', droppable: false });
            groups.set(UNCATEGORIZED, orphans);
        }
        return { columns: cols, byCategory: groups };
    }, [files, categories, activeCategories]);

    const collisionDetection = useMemo(
        // Only real categories are drop targets: "uncategorized" isn't a
        // category you can move something *into*.
        () => createContainerCollisionDetection(columns.filter((c) => c.droppable !== false).map((c) => c.id)),
        [columns]
    );

    const activeFile = activeId ? files.find((f) => f.id === activeId) : null;

    const handleDragEnd = ({ active, over }) => {
        setActiveId(null);
        if (!over || readOnly) return;
        const file = files.find((f) => f.id === active.id);
        if (file && over.id !== file.category && over.id !== UNCATEGORIZED) {
            onSetCategory(file, over.id);
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={({ active }) => !readOnly && setActiveId(active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            {/* Scrolls at every width. The scrollbar is deliberately left visible
                on desktop (the global style makes it an 8px rail; it's hidden
                under 768px) — it's the only signal that there are more columns
                off to the right. */}
            <div className="flex min-h-0 flex-1 snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-4 touch-pan-x sm:grid sm:auto-cols-[minmax(15rem,1fr)] sm:grid-flow-col sm:snap-none md:px-0 md:pb-0">
                {columns.map((col) => (
                    <div
                        key={col.id}
                        className="flex h-full min-h-0 w-[85vw] max-w-[85vw] flex-shrink-0 snap-center flex-col sm:w-auto sm:min-w-0 sm:max-w-none"
                    >
                        <ShelfColumn
                            column={col}
                            files={byCategory.get(col.id) || []}
                            rowProps={rowProps}
                            openFileId={openFileId}
                            isChecked={isChecked}
                            readOnly={readOnly}
                            isDragging={!!activeId}
                        />
                    </div>
                ))}
            </div>

            {/* Portalled to <body>: the surface clips its content, so an in-place
                overlay is cut off the moment you drag toward a column edge. */}
            {createPortal(
                <DragOverlay>
                    {activeFile ? (
                        <div className="flex items-center gap-2 rounded-2xl border border-primary/50 bg-secondary/90 p-2 shadow-2xl shadow-black/50">
                            {getFileIcon(activeFile.type, activeFile.name, 'h-4 w-4')}
                            <span className="truncate font-mono text-xs font-medium">{activeFile.name}</span>
                        </div>
                    ) : null}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
}

function ShelfColumn({ column, files, rowProps, openFileId, isChecked, readOnly, isDragging }) {
    const droppable = column.droppable !== false;
    const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: !droppable || readOnly });

    return (
        <Surface
            ref={droppable ? setNodeRef : undefined}
            variant="tile"
            className={cn(
                'h-full transition-all duration-200',
                isOver && 'border-primary ring-2 ring-primary/30 scale-[1.02]',
                !isOver && isDragging && droppable && 'border-dashed border-primary/30'
            )}
        >
            {/* The drop wash rides its own layer: a bg-* in the Surface className
                is in the same twMerge group as the tile material and would
                replace it outright, leaving raw canvas mid-drag. */}
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-0 transition-colors duration-200',
                    isOver && 'bg-primary/10'
                )}
            />

            <div className="relative flex h-9 flex-shrink-0 items-center gap-2 border-b border-white/[0.06] px-3">
                <h2 className="truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {column.label}
                </h2>
                <span className="rounded-full bg-white/5 px-1.5 text-[10px] tabular-nums text-muted-foreground">
                    {files.length}
                </span>
            </div>

            <div className="relative min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1.5">
                {files.length === 0 ? (
                    /* Small on purpose: an empty column is a place to drop
                       something, not an announcement. */
                    <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
                        <FolderOpen className="h-4 w-4 text-muted-foreground/25" />
                        <p className="text-[10px] text-muted-foreground/50">
                            {droppable && !readOnly ? 'Drop an artifact here' : 'Nothing here'}
                        </p>
                    </div>
                ) : (
                    files.map((file) => (
                        <ShelfRow
                            key={file.id}
                            file={file}
                            rowProps={rowProps}
                            isSelected={openFileId === file.id}
                            isChecked={isChecked(file.id)}
                            readOnly={readOnly}
                        />
                    ))
                )}
            </div>
        </Surface>
    );
}

function ShelfRow({ file, rowProps, isSelected, isChecked, readOnly }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: file.id, disabled: readOnly });

    return (
        <div ref={setNodeRef} className={cn(isDragging && 'opacity-40')}>
            <FileRow
                {...rowProps}
                file={file}
                compact
                dragHandle={readOnly ? null : <DragHandle {...attributes} {...listeners} />}
                isSelected={isSelected}
                isChecked={isChecked}
            />
        </div>
    );
}
